import { useEffect, useRef } from 'react';
import { previewMode } from '../mobile/preview';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { io } from 'socket.io-client';
import client from '../api/client';
import { updateSessionStatus, setSessionQR, setSyncProgress, fetchSessions } from '../store/sessionSlice';
import { fetchContacts, fetchMessages } from '../store/messageSlice';

export default function useSocket() {
  const dispatch = useDispatch();
  const store = useStore();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const connectionIds = useSelector(state => state.sessions.items.map(s => s.session_id).sort().join(','));
  const socketRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || !user || previewMode) return;
    let stopped = false;
    let retry;
    let refresh;
    let syncing = false;
    let dirty = false;
    let cursor = 0;
    const cursorKey = `what:sync:${user.id}`;
    try { cursor = Number(sessionStorage.getItem(cursorKey)) || 0; } catch (_) {}
    // Ricarica solo quello che le notifiche dicono cambiato. `tutto` quando il
    // cursore non vale più (scaduto o assente): non si sa cosa si è perso.
    const aggiorna = (note, tutto) => {
      if (stopped || (!tutto && !note.length)) return;
      const selected = store.getState().messages.selectedContact;
      const messaggi = note.filter(n => n.kind === 'message.updated');
      if (tutto || note.some(n => n.kind === 'connection.updated')) dispatch(fetchSessions());
      if (tutto || messaggi.length) dispatch(fetchContacts());
      if (selected && (tutto || messaggi.some(n => (n.payload?.connection ?? selected.connection) === selected.connection))) {
        dispatch(fetchMessages(selected.id));
      }
    };
    let ultimoRecupero = 0;
    const recover = async () => {
      if (syncing) { dirty = true; return; }
      syncing = true;
      try {
        do {
          dirty = false;
          ultimoRecupero = Date.now();
          let more;
          let tutto = false;
          const note = [];
          do {
            const { data } = await client.get('/v1/sync/', { params: { after: cursor, recover: 1 } });
            if (stopped) return;
            cursor = data.cursor;
            try { sessionStorage.setItem(cursorKey, String(cursor)); } catch (_) {}
            tutto = tutto || data.reset_required;
            note.push(...(data.notifications || []));
            more = data.has_more;
          } while (more && !stopped);
          aggiorna(note, tutto);
        } while (dirty && !stopped);
      } catch (_) { /* Retry through periodic recovery or the next connection. */ }
      finally { syncing = false; }
    };
    // Rete di sicurezza, se una notifica si perde: ogni 2 minuti col socket
    // collegato, ogni 30 secondi se è caduto; mai con la scheda nascosta.
    const recoveryTimer = setInterval(() => {
      if (document.hidden) return;
      if (socket.connected && Date.now() - ultimoRecupero < 120000) return;
      recover();
    }, 30000);
    const tornaVisibile = () => { if (!document.hidden) recover(); };
    document.addEventListener('visibilitychange', tornaVisibile);
    const socket = io(window.location.origin, { path: '/ws/', transports: ['websocket'], autoConnect: false, reconnection: false });
    const connect = async () => {
      try {
        const { data } = await client.post('/v1/realtime/ticket/');
        if (stopped) return;
        socket.auth = { ticket: data.ticket };
        socket.connect();
      } catch (_) { if (!stopped) retry = setTimeout(connect, 5000); }
    };
    const reconnect = () => {
      clearTimeout(retry);
      if (!stopped) retry = setTimeout(connect, 2000);
    };
    socket.on('connect_error', reconnect);
    socket.on('disconnect', reconnect);
    socket.on('connect', recover);
    socket.on('notification', () => {
      clearTimeout(refresh); refresh = setTimeout(recover, 350);
    });
    socket.on('qr', payload => dispatch(setSessionQR(payload)));
    socket.on('sync:progress', payload => dispatch(setSyncProgress(payload)));
    socket.on('session:status', payload => dispatch(updateSessionStatus(payload)));
    socketRef.current = socket;
    connect();
    return () => {
      stopped = true; clearTimeout(retry); clearTimeout(refresh); clearInterval(recoveryTimer);
      document.removeEventListener('visibilitychange', tornaVisibile);
      socket.disconnect(); socketRef.current = null;
    };
  }, [isAuthenticated, user, connectionIds, dispatch, store]);
  return socketRef;
}
